import React, {useState} from 'react';
import {CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useNavigate} from 'react-router-dom';
import {useRegisterBusinessProfileHandler} from '../businessProfiles/dataHandlers/registerBusinessProfileHandler';
import {useDeregisterBusinessProfileHandler} from '../businessProfiles/dataHandlers/deregisterBusinessProfileHandler';
import BusinessProfileDeregisterDialog from '../businessProfiles/components/BusinessProfileDeregisterDialog';
import {canDeregisterBusinessProfile, canRegisterBusinessProfile} from "../../constants/businessProfileStatusConstants";
import {usePermissions} from "../../hooks/usePermissions";
import {UPDATE_BUSINESS_PROFILE} from "../../constants/eInvoicePermissions";
import PermissionDeniedWrapper from "../../components/PermissionDeniedWrapper";
import {useAccessDeniedMessage} from '../../hooks/useAccessDeniedMessage';
import {useSelector} from "react-redux";

const BusinessProfileActions = ({profile, documentApiBaseKey}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [deregisterDialogOpen, setDeregisterDialogOpen] = useState(false);
    const navigate = useNavigate();
    const {hasEInvoicePermission} = usePermissions();
    const accessDeniedMessage = useAccessDeniedMessage();
    const canUpdate = hasEInvoicePermission(UPDATE_BUSINESS_PROFILE);
    const isRootUser = useSelector(state => state?.user?.user?.is_root_user);

    const profileId = profile?.profile_id;
    const {registerBusinessProfile, isPending: isRegisterPending} = useRegisterBusinessProfileHandler();
    const {deregisterBusinessProfile, isPending: isDeregisterPending} = useDeregisterBusinessProfileHandler({
        onSuccess: () => setDeregisterDialogOpen(false),
    });

    const showRegister = canRegisterBusinessProfile(profile) && isRootUser;
    const showDeregister = canDeregisterBusinessProfile(profile) && isRootUser;
    const peppolIdentifier = `${profile?.endpoint_scheme_id || '0248'}:${profile?.endpoint_id || ''}`;
    const isActionPending = isRegisterPending || isDeregisterPending;

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const onClickingDetails = () => {
        handleCloseMenu();
        navigate(`/${documentApiBaseKey}/${profileId}`);
    };

    const onClickingEdit = () => {
        handleCloseMenu();
        navigate(`/${documentApiBaseKey}/${profileId}/edit`);
    };

    const onClickingRegister = () => {
        handleCloseMenu();
        registerBusinessProfile(profileId);
    };

    const onClickingDeregister = () => {
        handleCloseMenu();
        setDeregisterDialogOpen(true);
    };

    const onConfirmDeregister = () => {
        deregisterBusinessProfile(profileId);
    };

    return (
        <>
            <IconButton id="action-icon-button" onClick={handleClick} disabled={isActionPending}>
                {isActionPending ? <CircularProgress size={20}/> : <MoreVertIcon/>}
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                slotProps={{
                    paper: {
                        style: {
                            boxShadow: '4px 4px 10px 0px rgba(0,0,0,0.2)',
                        },
                    },
                }}
            >
                <MenuItem id="details" onClick={onClickingDetails}>
                    Details
                </MenuItem>
                <PermissionDeniedWrapper show={!canUpdate} title={accessDeniedMessage}>
                    <MenuItem id="edit" onClick={onClickingEdit} disabled={!canUpdate}>
                        Edit
                    </MenuItem>
                </PermissionDeniedWrapper>

                {showRegister && (
                    <MenuItem id="register" onClick={onClickingRegister} disabled={isRegisterPending}>
                        <ListItemText>Register to Oman E-Invoicing Network</ListItemText>
                        {isRegisterPending && (
                            <ListItemIcon sx={{minWidth: 'auto', ml: 1}}>
                                <CircularProgress size={16}/>
                            </ListItemIcon>
                        )}
                    </MenuItem>
                )}
                {showDeregister && (
                    <MenuItem id="deregister" onClick={onClickingDeregister} disabled={isDeregisterPending}>
                        Deregister from Oman E-Invoicing Network
                    </MenuItem>
                )}
            </Menu>

            <BusinessProfileDeregisterDialog
                open={deregisterDialogOpen}
                onClose={() => setDeregisterDialogOpen(false)}
                onConfirm={onConfirmDeregister}
                peppolIdentifier={peppolIdentifier}
                isPending={isDeregisterPending}
            />
        </>
    );
};

export default BusinessProfileActions;
